import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../auth/index.js';
import type { PlatformEvent } from '../api/types.js';

const API_URL = import.meta.env.VITE_API_URL || '';
const MAX_BACKOFF_MS = 30_000;

export type SSEStatus = 'connecting' | 'open' | 'closed' | 'error';

export interface UseSSEResult {
  status: SSEStatus;
  close: () => void;
}

export function useSSE(onEvent: (event: PlatformEvent) => void): UseSSEResult {
  const { isAuthenticated, getIdToken } = useAuth();
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const abortRef = useRef<AbortController | null>(null);
  const lastEventIdRef = useRef<string | null>(null);
  const [status, setStatus] = useState<SSEStatus>('connecting');

  const connect = useCallback(async (retryCount: number) => {
    if (abortRef.current) abortRef.current.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    setStatus('connecting');

    let token: string;
    try {
      token = await getIdToken();
    } catch {
      if (!controller.signal.aborted) setStatus('error');
      return;
    }

    try {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };
      if (lastEventIdRef.current !== null) {
        headers['Last-Event-ID'] = lastEventIdRef.current;
      }

      const response = await fetch(`${API_URL}/events`, {
        headers,
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      setStatus('open');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // Reset retry count on successful connection
      retryCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        let currentEvent = '';
        let currentData = '';
        let currentId = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
          } else if (line.startsWith('id: ')) {
            currentId = line.slice(4);
          } else if (line === '' && currentData) {
            if (currentId) lastEventIdRef.current = currentId;
            try {
              const parsed = JSON.parse(currentData) as PlatformEvent;
              if (currentEvent) parsed.type = currentEvent as PlatformEvent['type'];
              onEventRef.current(parsed);
            } catch {
              // Skip malformed events
            }
            currentEvent = '';
            currentData = '';
            currentId = '';
          }
          // Ignore heartbeat lines (: comments)
        }
      }
    } catch {
      if (controller.signal.aborted) return;
      setStatus('error');
    }

    // Reconnect with exponential backoff
    if (!controller.signal.aborted) {
      const base = Math.min(1000 * Math.pow(2, retryCount), MAX_BACKOFF_MS);
      const delay = base * (0.5 + Math.random() * 0.5);
      setTimeout(() => {
        if (!controller.signal.aborted) {
          connect(retryCount + 1);
        }
      }, delay);
    }
  }, [getIdToken]);

  const close = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStatus('closed');
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    connect(0);

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [isAuthenticated, connect]);

  return { status, close };
}
